import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  Label,
  SearchableCombobox,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Switch,
  Textarea,
} from "@nexus/ui";
import { TRIGGERABLE_EVENTS, findTriggerableEvent } from "@nexus/contracts";
import { useCreateScheduledJob } from "@/lib/queries";
import { buildAdvancedPayload, isAdvancedPubsubDirty } from "../lib/scheduled-job-advanced";
import { ScheduledJobMutationError } from "./ScheduledJobMutationError";
import {
  deriveAdvancedScheduledJobPreview,
  deriveScheduledJobPreview,
  hasEventTypeAttribute,
  isValidScheduledJobId,
} from "../lib/scheduled-job-preview";
import {
  advancedFormFromJob,
  ScheduledJobAdvancedFields,
  type ScheduledJobAdvancedFormState,
} from "./ScheduledJobAdvancedFields";
import { isValidCronSchedule, ScheduledJobCronField } from "./ScheduledJobCronField";
import { ScheduledJobPreview } from "./ScheduledJobPreview";
import { ScheduledJobTimezoneField } from "./ScheduledJobTimezoneField";

interface ScheduledJobCreateSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (jobId: string) => void;
}

const DEFAULT_SCHEDULE = "0 * * * *";
const DEFAULT_TIME_ZONE = "UTC";
const DESCRIPTION_ROWS = 5;

function seedAdvancedFromEvent(eventName: string): ScheduledJobAdvancedFormState {
  const curated = deriveScheduledJobPreview(eventName);
  const entry = findTriggerableEvent(eventName);
  const bodyJson = entry
    ? {
        id: "cloud-scheduler",
        eventType: eventName,
        payload: entry.sample,
      }
    : { id: "cloud-scheduler", eventType: eventName, payload: {} };
  return advancedFormFromJob({
    topicName: curated?.topic ?? "",
    attributes: curated?.attributes ?? { eventType: eventName },
    bodyJson,
    retryConfig: null,
  });
}

export function ScheduledJobCreateSheet({
  isOpen,
  onOpenChange,
  onCreated,
}: ScheduledJobCreateSheetProps) {
  const { t } = useTranslation();
  const create = useCreateScheduledJob();

  const [jobId, setJobId] = useState("");
  const [eventName, setEventName] = useState<string>(TRIGGERABLE_EVENTS[0]?.eventName ?? "");
  const [description, setDescription] = useState("");
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [timeZone, setTimeZone] = useState(DEFAULT_TIME_ZONE);
  const [enabled, setEnabled] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advanced, setAdvanced] = useState<ScheduledJobAdvancedFormState>(() =>
    seedAdvancedFromEvent(TRIGGERABLE_EVENTS[0]?.eventName ?? ""),
  );
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const initialEvent = TRIGGERABLE_EVENTS[0]?.eventName ?? "";
    setJobId("");
    setEventName(initialEvent);
    setDescription("");
    setSchedule(DEFAULT_SCHEDULE);
    setTimeZone(DEFAULT_TIME_ZONE);
    setEnabled(true);
    setAdvancedOpen(false);
    setAdvanced(seedAdvancedFromEvent(initialEvent));
    setFormError(null);
  }, [isOpen]);

  function handleOpenChange(open: boolean) {
    if (create.isPending) return;
    if (!open) create.reset();
    onOpenChange(open);
  }

  function handleEventSelect(name: string) {
    setEventName(name);
    // Always reseed Advanced from the curated catalog so picker and preview stay aligned.
    setAdvanced(seedAdvancedFromEvent(name));
  }

  function handleAdvancedOpenChange(open: boolean) {
    if (open && !advancedOpen) {
      setAdvanced(seedAdvancedFromEvent(eventName));
    }
    setAdvancedOpen(open);
  }

  const curatedPreview = useMemo(() => deriveScheduledJobPreview(eventName), [eventName]);
  const advancedSeed = useMemo(() => seedAdvancedFromEvent(eventName), [eventName]);
  const includePubsub = advancedOpen && isAdvancedPubsubDirty(advanced, advancedSeed);
  const advancedPreview = useMemo(
    () =>
      deriveAdvancedScheduledJobPreview({
        topicName: advanced.topicName,
        attributesJson: advanced.attributesJson,
        bodyJson: advanced.bodyJson,
      }),
    [advanced.topicName, advanced.attributesJson, advanced.bodyJson],
  );
  const preview = advancedOpen ? advancedPreview : curatedPreview;
  const warnMissingEventType =
    includePubsub && !hasEventTypeAttribute(advancedPreview.attributes);

  const eventGroups = useMemo(
    () => [
      {
        heading: t("admin.scheduledJobs.create.eventGroup", "Triggerable events"),
        items: TRIGGERABLE_EVENTS.map((entry) => entry.eventName),
      },
    ],
    [t],
  );

  const selectedSummary = TRIGGERABLE_EVENTS.find((entry) => entry.eventName === eventName)
    ?.summary;

  const canSubmit =
    !create.isPending &&
    isValidScheduledJobId(jobId.trim()) &&
    !!curatedPreview &&
    isValidCronSchedule(schedule) &&
    timeZone.trim().length > 0 &&
    !warnMissingEventType;

  function advancedFormErrorMessage(
    error: "attributes" | "body" | "retry" | "attemptDeadline" | "incomplete" | "eventType" | "topic",
  ): string {
    switch (error) {
      case "attributes":
        return t(
          "admin.scheduledJobs.advanced.attributesInvalid",
          "Attributes must be a JSON object of string values.",
        );
      case "body":
        return t("admin.scheduledJobs.advanced.bodyInvalid", "Body must be valid JSON.");
      case "retry":
        return t(
          "admin.scheduledJobs.advanced.retryInvalid",
          "Retry fields must be non-negative integers.",
        );
      case "attemptDeadline":
        return t(
          "admin.scheduledJobs.advanced.attemptDeadlineInvalid",
          "Attempt deadline must be an integer between 15 and 1800 seconds.",
        );
      case "eventType":
        return t(
          "admin.scheduledJobs.advanced.eventTypeRequired",
          "Attributes must include a non-empty eventType.",
        );
      case "topic":
        return t(
          "admin.scheduledJobs.advanced.topicInvalid",
          "Topic must be a full resource name starting with projects/.",
        );
      default:
        return t(
          "admin.scheduledJobs.advanced.incomplete",
          "Topic, attributes, and body must be provided together.",
        );
    }
  }

  function submit() {
    if (!canSubmit || !curatedPreview) return;
    setFormError(null);

    const advancedResult = buildAdvancedPayload({
      open: advancedOpen,
      form: advanced,
      includePubsub,
    });
    if (!advancedResult.ok) {
      setFormError(advancedFormErrorMessage(advancedResult.error));
      return;
    }

    const trimmedId = jobId.trim();
    create.mutate(
      {
        id: trimmedId,
        eventName: curatedPreview.eventType ?? eventName,
        description: description.trim() ? description.trim() : null,
        schedule: schedule.trim(),
        timeZone: timeZone.trim(),
        enabled,
        ...(advancedResult.value ? { advanced: advancedResult.value } : {}),
      },
      {
        onSuccess: () => {
          handleOpenChange(false);
          onCreated?.(trimmedId);
        },
      },
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>
            {t("admin.scheduledJobs.create.title", "Create scheduled job")}
          </SheetTitle>
          <SheetDescription>
            {t(
              "admin.scheduledJobs.create.subtitle",
              "Pick a triggerable Nexus event. Topic and eventType are filled for you.",
            )}
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <Label htmlFor="scheduled-job-create-event">
              {t("admin.scheduledJobs.create.event", "Event")}
            </Label>
            <SearchableCombobox
              groups={eventGroups}
              getKey={(name) => name}
              getLabel={(name) => name}
              value={eventName || null}
              onSelect={handleEventSelect}
              disabled={create.isPending}
              placeholder={t(
                "admin.scheduledJobs.create.eventPlaceholder",
                "Pick a triggerable event…",
              )}
              searchPlaceholder={t(
                "admin.scheduledJobs.create.eventSearch",
                "Search events…",
              )}
              emptyMessage={t(
                "admin.scheduledJobs.create.eventEmpty",
                "No triggerable event matches.",
              )}
              ariaLabel={t("admin.scheduledJobs.create.event", "Event")}
              triggerClassName="font-mono text-[11px]"
              itemClassName="font-mono text-[11px]"
            />
            {selectedSummary ? (
              <p className="text-xs text-muted-foreground">{selectedSummary}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="scheduled-job-create-id">
              {t("admin.scheduledJobs.create.jobId", "Job id")}
            </Label>
            <Input
              id="scheduled-job-create-id"
              value={jobId}
              disabled={create.isPending}
              spellCheck={false}
              autoComplete="off"
              placeholder={t(
                "admin.scheduledJobs.create.jobIdPlaceholder",
                "admin-ops-sweep",
              )}
              onChange={(event) => setJobId(event.target.value)}
              className="font-mono text-[11px]"
            />
            <p className="text-[11px] text-muted-foreground">
              {t(
                "admin.scheduledJobs.create.jobIdHint",
                "Immutable after create. Letters, digits, hyphens, and underscores; must start with a letter.",
              )}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="scheduled-job-create-description">
              {t("admin.scheduledJobs.create.description", "Description")}
            </Label>
            <Textarea
              id="scheduled-job-create-description"
              value={description}
              rows={DESCRIPTION_ROWS}
              disabled={create.isPending}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t(
                "admin.scheduledJobs.drawer.descriptionPlaceholder",
                "What this job does, who owns it, and what to check when it fails.",
              )}
              aria-describedby="scheduled-job-create-description-hint"
              className="min-h-[7.5rem] resize-y text-xs leading-5"
            />
            <p
              id="scheduled-job-create-description-hint"
              className="text-[11px] leading-4 text-muted-foreground"
            >
              {t(
                "admin.scheduledJobs.drawer.descriptionHint",
                "Shown in the job list and Slack lifecycle alerts. Prefer outcome and owner over restating the cron.",
              )}
            </p>
          </div>

          <ScheduledJobCronField
            id="scheduled-job-create-schedule"
            value={schedule}
            disabled={create.isPending}
            onChange={setSchedule}
          />
          <div className="sm:max-w-xs">
            <ScheduledJobTimezoneField
              id="scheduled-job-create-timezone"
              value={timeZone}
              disabled={create.isPending}
              placeholder={DEFAULT_TIME_ZONE}
              onChange={setTimeZone}
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="scheduled-job-create-enabled">
                {t("admin.scheduledJobs.create.enabled", "Enabled")}
              </Label>
              <p className="text-[11px] text-muted-foreground">
                {t(
                  "admin.scheduledJobs.create.enabledHint",
                  "Off creates the job paused.",
                )}
              </p>
            </div>
            <Switch
              id="scheduled-job-create-enabled"
              checked={enabled}
              disabled={create.isPending}
              onCheckedChange={setEnabled}
            />
          </div>

          <ScheduledJobAdvancedFields
            idPrefix="scheduled-job-create"
            open={advancedOpen}
            onOpenChange={handleAdvancedOpenChange}
            value={advanced}
            onChange={setAdvanced}
            disabled={create.isPending}
            showPubsubFields
          />

          <ScheduledJobPreview preview={preview} warnMissingEventType={warnMissingEventType} />

          {formError ? (
            <p className="rounded-md bg-error-subtle px-3 py-2 text-xs text-error-subtle-foreground">
              {formError}
            </p>
          ) : null}

          {create.error ? (
            <ScheduledJobMutationError error={create.error} onDismiss={() => create.reset()} />
          ) : null}
        </SheetBody>

        <SheetFooter className="justify-end px-6">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 rounded-sm text-xs"
            disabled={create.isPending}
            onClick={() => handleOpenChange(false)}
          >
            {t("admin.scheduledJobs.create.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-8 rounded-sm text-xs"
            disabled={!canSubmit}
            onClick={submit}
          >
            {create.isPending
              ? t("admin.scheduledJobs.create.creating", "Creating…")
              : t("admin.scheduledJobs.create.confirm", "Create job")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
